import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui';
import { Badge } from '@/components/ui';
import { Progress } from '@/components/ui';
import { GBPChecklist } from './GBPChecklist';
import { GBPPostSuggestions } from './GBPPostSuggestions';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { getGBPData } from '@/data/mocks/local';
import {
  MapPin,
  Star,
  Image as ImageIcon,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Clock,
  FileText
} from 'lucide-react';

interface LocalSectionProps {
  restaurantId: string;
}

export const LocalSection: React.FC<LocalSectionProps> = ({ restaurantId }) => {
  const data = getGBPData(restaurantId);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">GMB / Local SEO</h2>
        <Badge
          variant={data.metrics.status === 'verified' ? 'success' : 'warning'}
        >
          {data.metrics.status}
        </Badge>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Complétude GBP</p>
                <p className="text-2xl font-bold">{data.metrics.completeness}%</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <Progress value={data.metrics.completeness} className="mt-4" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Avis Google</p>
                <p className="text-2xl font-bold">{data.metrics.reviews.total}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              Note moyenne : {data.metrics.reviews.average}/5
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Posts Mensuels</p>
                <p className="text-2xl font-bold">
                  {data.metrics.posts.published}/{data.metrics.posts.planned}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <Progress
              value={(data.metrics.posts.published / data.metrics.posts.planned) * 100}
              className="mt-4"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Engagement</p>
                <p className="text-2xl font-bold">
                  {data.metrics.posts.engagement.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <MessageSquare className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Checklist d'optimisation */}
      <Card>
        <CardHeader>
          <CardTitle>Checklist d'Optimisation GBP</CardTitle>
          <CardDescription>État d'avancement des optimisations</CardDescription>
        </CardHeader>
        <CardContent>
          <GBPChecklist items={data.checklist} />
        </CardContent>
      </Card>

      {/* Suggestions de Posts */}
      <Card>
        <CardHeader>
          <CardTitle>Suggestions de Posts GBP</CardTitle>
          <CardDescription>Posts multilingues générés automatiquement</CardDescription>
        </CardHeader>
        <CardContent>
          <GBPPostSuggestions suggestions={data.postSuggestions} />
        </CardContent>
      </Card>

      {/* Graphique d'activité */}
      <Card>
        <CardHeader>
          <CardTitle>Activité Mensuelle GBP</CardTitle>
          <CardDescription>Suivi des posts, avis et réponses</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.activity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('fr-FR', { month: 'short' });
                  }}
                />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="posts"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6' }}
                  name="Posts"
                />
                <Line
                  type="monotone"
                  dataKey="reviews"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={{ fill: '#10B981' }}
                  name="Avis"
                />
                <Line
                  type="monotone"
                  dataKey="responses"
                  stroke="#F59E0B"
                  strokeWidth={2}
                  dot={{ fill: '#F59E0B' }}
                  name="Réponses"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
